/**
 * web-sign-in-checkout D5, D8 — registration and the studio's forms on the website session.
 *
 * The website's counterpart of the app's details page logic (`DetailsPage`):
 * which form is next (the shared `useFormsFlow`), its definition, autosave,
 * submit, and Back (one form back, then out to the offering). The same
 * `/api/webapp/forms` routes on the website cookie; no Telegram.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WebAppFlowContextResponse } from '@/api/types';
import { useFormsFlow } from '@/hooks/useFormsFlow';
import {
  formDefinitionSchema,
  type FieldError,
  type FormDefinition,
  type FormResponseInput,
  type FormSubmissionSuccess,
} from '@/types/forms';

import { CLIENT_SCOPE, SiteFetchError, webFetch } from '../api/session';
import { usePageMover } from '../pageMover';
import { flowKeys, newIdempotencyKey, webFlow } from './useWebFlow';

const formKey = (flowToken: string, formId: string) => [...CLIENT_SCOPE, 'form', flowToken, formId] as const;
const registrationKey = (flowToken: string) => [...CLIENT_SCOPE, 'flow-registration', flowToken] as const;

const post = <T>(path: string, body: unknown, headers?: Record<string, string>) =>
  webFetch<T>(path, { method: 'POST', body: JSON.stringify(body), headers });

/** The field errors of a refused submit, when the server named them (the route answers them at the top level). */
function fieldErrorsOf(error: unknown): FieldError[] | undefined {
  if (!(error instanceof SiteFetchError) || error.code !== 'VALIDATION_FAILED') return undefined;
  const body = error.body as { fieldErrors?: FieldError[]; details?: { fieldErrors?: FieldError[] } };
  const errors = body.fieldErrors ?? body.details?.fieldErrors;
  return errors && errors.length > 0 ? errors : undefined;
}

/** The line above the button when the answers could not be sent and no field says why (Codex review 2026-10-26). */
export const SUBMIT_FAILED_TEXT = "Couldn't send your answers. Please try again.";

export interface FormPage {
  /** The form drawn now; undefined while it loads. */
  readonly form: FormDefinition | undefined;
  /** 1-based place of this form among all the client owes (registration counted). */
  readonly index: number;
  readonly total: number;
  /** True when payment follows the forms. */
  readonly paymentAhead: boolean;
  readonly submitting: boolean;
  readonly fieldErrors: FieldError[] | undefined;
  /** The answers could not be sent and no field says why; null otherwise. */
  readonly submitError: string | null;
  /** The form could not be read. */
  readonly failed: boolean;
  /** Why the flow names forms it cannot show (the app's "Unable to Load Forms"); null when it can. */
  readonly formsError: string | null;
  /** The form itself could not be read (the app's "Unable to Load Form"). */
  readonly formFailed: boolean;
  /** True while a step back is under way: Back is off until it lands (row R5). */
  readonly backPending: boolean;
  /** Where Back goes from here, in words: the form before, or the offering. */
  readonly backLabel: string;
  readonly submit: (responses: FormResponseInput[], extra?: { privacyAccepted?: boolean }) => Promise<boolean>;
  readonly save: (responses: FormResponseInput[]) => void;
  readonly back: () => void;
}

/**
 * The form the client owes now in the held flow `flowToken`. `offering` names
 * the offering's page and name, for Back from the first form.
 */
export function useFormPage(
  flowToken: string,
  flowContext: WebAppFlowContextResponse,
  offering: { name: string; path: string } | null,
): FormPage {
  const queryClient = useQueryClient();
  const navigate = usePageMover();
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Registration done in this checkout still counts among its forms ("2 of 3 forms", phone-checkout.html frame C),
  // though the server stops naming the registration form once the client is registered.
  const regKey = registrationKey(flowToken);
  const pending = !flowContext.user.isRegistered ? flowContext.registrationFormId : null;
  useEffect(() => {
    if (pending) queryClient.setQueryData(regKey, pending);
  }, [pending, queryClient, regKey]);
  const doneHere = pending ? null : (queryClient.getQueryData<string>(regKey) ?? null);
  const counted = useMemo(
    () => (doneHere ? { ...flowContext, user: { ...flowContext.user, isRegistered: false }, registrationFormId: doneHere } : flowContext),
    [doneHere, flowContext],
  );
  const shown = useMemo(() => (doneHere ? new Set(completed).add(doneHere) : completed), [doneHere, completed]);
  const flow = useFormsFlow({ flowContext: counted, completedFormIds: shown, currentStep: flowContext.flow.currentStep });
  const formId = flow.currentFormId;

  const definition = useQuery({
    queryKey: formKey(flowToken, formId ?? ''),
    queryFn: async () =>
      formDefinitionSchema.parse(
        await webFetch<unknown>(`/forms/${encodeURIComponent(formId!)}?${new URLSearchParams({ flowToken })}`),
      ) as FormDefinition,
    enabled: Boolean(formId) && !flow.isError,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // A form already submitted (a reload past it) counts as done.
  useEffect(() => {
    const submitted = definition.data?.submission ? definition.data.formId : null;
    if (submitted) setCompleted((prev) => new Set(prev).add(submitted));
  }, [definition.data]);

  const submitMutation = useMutation({
    mutationFn: (v: { formId: string; responses: FormResponseInput[]; privacyAccepted?: boolean }) =>
      post<FormSubmissionSuccess>(
        `/forms/${encodeURIComponent(v.formId)}/submit`,
        { flowToken, responses: v.responses, ...(v.privacyAccepted ? { privacyAccepted: true } : {}) },
        { 'Idempotency-Key': newIdempotencyKey() },
      ),
  });

  // Autosave, half a second after the last change (registration does not save partway).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSave = useRef<{ formId: string; responses: FormResponseInput[] } | null>(null);
  const flushSave = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = null;
    const pending = pendingSave.current;
    pendingSave.current = null;
    if (pending) await post(`/forms/${encodeURIComponent(pending.formId)}/save`, { flowToken, responses: pending.responses }).catch(() => undefined);
  }, [flowToken]);
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const save = useCallback(
    (responses: FormResponseInput[]) => {
      if (!formId) return;
      pendingSave.current = { formId, responses };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void flushSave(), 500);
    },
    [formId, flushSave],
  );

  const submit = useCallback(
    async (responses: FormResponseInput[], extra?: { privacyAccepted?: boolean }) => {
      if (!formId) return false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      pendingSave.current = null;
      setSubmitError(null);
      try {
        const result = await submitMutation.mutateAsync({ formId, responses, privacyAccepted: extra?.privacyAccepted });
        setCompleted((prev) => new Set(prev).add(formId));
        queryClient.setQueryData<WebAppFlowContextResponse>(flowKeys.context(flowToken), (old) =>
          old ? { ...old, flow: { ...old.flow, currentStep: result.nextStep } } : old,
        );
        void queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
        return true;
      } catch (error) {
        // Already in (an earlier send landed though its answer was lost): the form is done, the flow read again.
        if (error instanceof SiteFetchError && error.code === 'ALREADY_SUBMITTED') {
          setCompleted((prev) => new Set(prev).add(formId));
          void queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
          return true;
        }
        if (fieldErrorsOf(error)) return false;
        // Anything else may or may not have landed: say so, and read the form and the flow again, so a
        // send that did land moves the checkout on by itself (the form's own submission marks it done).
        setSubmitError(SUBMIT_FAILED_TEXT);
        void queryClient.invalidateQueries({ queryKey: formKey(flowToken, formId) });
        void queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
        return false;
      }
    },
    [formId, flowToken, submitMutation, queryClient],
  );

  // Back: the form before; from the first studio form to registration; from the first form of all, out to the offering.
  const onRegistration = Boolean(flow.registrationFormId) && formId === flow.registrationFormId;
  const idx = formId ? flow.requiredForms.findIndex((f) => f.formId === formId) : -1;
  const before = idx > 0 ? flow.requiredForms[idx - 1] : null;
  const [backPending, setBackPending] = useState(false);
  const backing = useRef(false);
  const back = useCallback(() => {
    if (backing.current) return;
    backing.current = true;
    setBackPending(true);
    const done = () => {
      backing.current = false;
      setBackPending(false);
    };
    void (async () => {
      if (before) {
        await flushSave();
        // The form left behind reloads with what was just saved when the client returns to it.
        if (formId) queryClient.removeQueries({ queryKey: formKey(flowToken, formId) });
        await post(`/forms/${encodeURIComponent(before.formId)}/prepare-edit`, { flowToken }).catch(() => undefined);
        queryClient.removeQueries({ queryKey: formKey(flowToken, before.formId) });
        setCompleted(new Set(flow.requiredForms.slice(0, idx - 1).map((f) => f.formId)));
        done();
        return;
      }
      if (!onRegistration && idx === 0 && pending) {
        await post(`/flow/${encodeURIComponent(flowToken)}/go-back`, {}).catch(() => undefined);
        await queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
        done();
        return;
      }
      // The first form of all: release the hold and return to the offering, as the app does.
      await webFlow.cancel(flowToken).catch(() => undefined);
      queryClient.removeQueries({ queryKey: flowKeys.context(flowToken) });
      navigate(offering?.path ?? '/', { replace: true });
    })();
  }, [before, flushSave, formId, flowToken, queryClient, flow, idx, onRegistration, pending, navigate, offering]);

  const backLabel = before
    ? `Back to ${before.formName}`
    : !onRegistration && idx === 0 && pending
      ? 'Back to registration'
      : `Back to ${offering?.name ?? 'the studio'}`;

  return {
    form: definition.data && !definition.data.submission ? definition.data : undefined,
    index: flow.currentFormIndex,
    total: flow.totalForms,
    paymentAhead: flowContext.progressSteps.some((s) => s.stepId === 'PAYMENT'),
    submitting: submitMutation.isPending,
    fieldErrors: submitMutation.error ? fieldErrorsOf(submitMutation.error) : undefined,
    submitError: submitError && !submitMutation.isPending ? submitError : null,
    failed: definition.isError || flow.isError,
    formsError: flow.isError ? flow.errorMessage : null,
    formFailed: definition.isError,
    backPending,
    backLabel,
    submit,
    save,
    back,
  };
}

/**
 * Back from the payment or review screen on the phone (design D12; the app's
 * payment and review pages go one step back): to the studio's last form when
 * the server steps back to the forms; otherwise the held time is released and
 * the visitor returns to the offering's page, a service with that time picked.
 */
export function useWebStepBack(flowToken: string, flowContext: WebAppFlowContextResponse, offeringPath: string | null) {
  const queryClient = useQueryClient();
  const navigate = usePageMover();
  const [pending, setPending] = useState(false);
  const start = flowContext.flow.selectedEventId ? null : (flowContext.flow.selectedSlot?.startsAt ?? null);
  const back = useCallback(() => {
    if (pending) return;
    setPending(true);
    void (async () => {
      const res = await post<{ currentStep: string; flowCancelled?: boolean }>(`/flow/${encodeURIComponent(flowToken)}/go-back`, {}).catch(() => null);
      if (res && !res.flowCancelled && res.currentStep === 'CUSTOM_FORMS') {
        queryClient.removeQueries({ queryKey: [...CLIENT_SCOPE, 'form', flowToken] });
        await queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
        setPending(false);
        return;
      }
      if (!res?.flowCancelled) await webFlow.cancel(flowToken).catch(() => undefined);
      queryClient.removeQueries({ queryKey: flowKeys.context(flowToken) });
      const path = offeringPath ?? '/';
      navigate(start && offeringPath ? `${path}?${new URLSearchParams({ start })}` : path, { replace: true });
    })();
  }, [pending, flowToken, queryClient, navigate, offeringPath, start]);
  return { back, pending };
}
